<template>
  <div class="custom-select-container">
    <!-- Label -->
    <label v-if="label" class="custom-select-label">
      {{ label }}
      <span v-if="required" class="required-asterisk">*</span>
    </label>

    <div
      class="custom-select-input"
      :class="{
        'disabled': disabled,
        'error': hasError,
        'focused': isOpen
      }"
      @click="toggleDropdown"
    >
      <div class="selected-value">
        <span v-if="!selectedOption" class="placeholder">{{ placeholder }}</span>
        <span v-else class="selected-text">{{ selectedOption.label }}</span>
      </div>
      <div class="select-arrow" :class="{ 'rotated': isOpen }">
        <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.85124 6.54162L0.715046 1.77848C0.226914 1.21806 0.623629 0.337891 1.36445 0.337891H9.63683C9.80263 0.337748 9.96495 0.385755 10.1044 0.476163C10.2438 0.566572 10.3544 0.69555 10.4229 0.847651C10.4914 0.999752 10.515 1.16853 10.4907 1.33377C10.4665 1.49901 10.3955 1.65371 10.2862 1.77935L6.15005 6.54075C6.0691 6.63408 5.96927 6.70888 5.85726 6.76013C5.74526 6.81138 5.62366 6.83789 5.50064 6.83789C5.37763 6.83789 5.25603 6.81138 5.14402 6.76013C5.03202 6.70888 4.93219 6.63408 4.85124 6.54075V6.54162Z" fill="#321C3B"/>
            </svg>

      </div>
    </div>

    <!-- Dropdown Options -->
    <transition name="dropdown">
      <div v-if="isOpen" class="custom-select-dropdown">
        <div class="dropdown-header" v-if="searchable">
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search..."
            @input="filterOptions"
            @click.stop
          />
        </div>
        <div class="options-container">

<div
v-for="option in filteredOptions"
:key="option[optionValue] ?? option"
class="option-item"
:class="{
  'disabled':  isOptionDisabled(option) || isExistingItem(option) || option.isApplied,
}"
@click.stop="selectOption(option)"
>
<div class="option-content">
  <span class="option-label">{{ option[optionLabel] ?? option.label }}</span>
    <Icon v-if="isOptionDisabled(option) || isExistingItem(option) || option.isApplied" icon-name="icon-check" size="xms" class-name="mr-2 w-5 h-5 ml-auto text-flame" />
</div>
</div>

        </div>
      </div>
    </transition>

    <!-- Error Message -->
    <div v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomSelect',
  props: {
    // Basic props
    value: {
      type: [String, Number, Object],
      default: null
    },
    options: {
      type: Array,
      default: () => [],
      required: true
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },

    // Customization props
    searchable: {
      type: Boolean,
      default: true
    },
    clearable: {
      type: Boolean,
      default: false
    },

    // Existing items handling
    existingItems: {
      type: Array,
      default: () => []
    },
    existingItemKey: {
      type: String,
      default: 'value'
    },

    // Validation
    rules: {
      type: Array,
      default: () => []
    },

    // Custom option rendering
    optionLabel: {
      type: String,
      default: 'label'
    },
    optionValue: {
      type: String,
      default: 'value'
    }
  },

  data() {
    return {
      isOpen: false,
      searchQuery: '',
      selectedOption: null,
      filteredOptions: [],
      hasError: false,
      errorMessage: ''
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.updateSelectedOption(newValue)
      }
    },
    options: {
      immediate: true,
      handler() {
        this.filteredOptions = [...this.options]
      }
    }
  },

  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
    this.filteredOptions = [...this.options]
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    updateSelectedOption(value) {
      if (!value) {
        this.selectedOption = null
        return
      }

      if (typeof value === 'object') {
        this.selectedOption = value
      } else {
        this.selectedOption = this.options.find(opt => opt[this.optionValue] === value) || null
      }
    },

    toggleDropdown() {
      if (this.disabled) return
      this.isOpen = !this.isOpen

      if (this.isOpen) {
        this.searchQuery = ''
        this.filteredOptions = [...this.options]
      }
    },

    selectOption(option) {
      if (this.isOptionDisabled(option)) return

      this.selectedOption = option
      this.isOpen = false
      this.searchQuery = ''

      // Emit the selected value
      const emitValue = this.optionValue ? option[this.optionValue] : option
      this.$emit('input', emitValue)
      this.$emit('change', emitValue, option)

      // Validate after selection
      this.validate()
    },

    isOptionSelected(option) {
      if (!this.selectedOption) return false

      if (this.optionValue) {
        return this.selectedOption[this.optionValue] === option[this.optionValue]
      }

      return this.selectedOption === option
    },

    isOptionDisabled(option) {
      // Check if option is in existing items
      if (this.existingItems && this.existingItems.length > 0) {
        return this.isExistingItem(option)
      }

      // Check if option has disabled property
      return option.disabled === true
    },

    isExistingItem(option) {
      if (!this.existingItems || this.existingItems.length === 0) return false

      const optionValue = this.optionValue ? option[this.optionValue] : option

      return this.existingItems.some(existing => {
        const existingValue = this.existingItemKey ? existing[this.existingItemKey] : existing
        return existingValue === optionValue
      })
    },

    filterOptions() {
      if (!this.searchQuery) {
        this.filteredOptions = [...this.options]
        return
      }

      const query = this.searchQuery.toLowerCase()
      this.filteredOptions = this.options.filter(option => {
        const label = this.optionLabel ? option[this.optionLabel] : option.label
        return label.toLowerCase().includes(query)
      })
    },

    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false
      }
    },

    validate() {
      if (!this.rules || this.rules.length === 0) {
        this.hasError = false
        this.errorMessage = ''
        return true
      }

      // Simple validation - you can extend this
      if (this.required && !this.selectedOption) {
        this.hasError = true
        this.errorMessage = 'This field is required'
        return false
      }

      this.hasError = false
      this.errorMessage = ''
      return true
    },

    clearSelection() {
      this.selectedOption = null
      this.$emit('input', null)
      this.$emit('change', null)
      this.validate()
    }
  }
}
</script>

<style scoped>
.custom-select-container {
  position: relative;
  width: 100%;
  font-family: inherit;
}

.custom-select-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.required-asterisk {
  color: #ef4444;
  margin-left: 4px;
}

.custom-select-input {
  position: relative;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.custom-select-input:hover:not(.disabled) {
  border-color: #9ca3af;
}

.custom-select-input.focused {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.custom-select-input.disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
  opacity: 0.6;
}

.custom-select-input.error {
  border-color: #ef4444;
}

.selected-value {
  flex: 1;
  min-width: 0;
}

.placeholder {
  color: #9ca3af;
}

.selected-text {
  color: #111827;
  font-weight: 500;
}

.select-arrow {
  transition: transform 0.2s ease;
  color: #6b7280;
}

.select-arrow.rotated {
  transform: rotate(180deg);
}

.custom-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 50;
  margin-top: 4px;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 300px;
  overflow: hidden;
}

.dropdown-header {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.search-input {
  width: 100%;
  padding: 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.options-container {
  max-height: 180px;
  overflow-y: auto;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f3f4f6;
}

.option-item:last-child {
  border-bottom: none;
}

.option-item:hover:not(.disabled) {
  background-color: #f9fafb;
}

.option-item.disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background-color: #878e9972;
  color: black;
  border-radius: 0px !important;
  border-top: none !important;
  border-right: none !important;
  border-left: none !important;
}

.option-item.existing {
  background-color: #878e9972;
}

.option-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.option-label {
  font-size: 14px;
}

.check-icon {
  color: #3b82f6;
}

.no-options {
  padding: 16px;
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

.error-message {
  margin-top: 4px;
  font-size: 12px;
  color: #ef4444;
}

/* Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Scrollbar styling */
.options-container::-webkit-scrollbar {
  width: 6px;
}

.options-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.options-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.options-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>